import React, { createContext, useState, useEffect, useContext } from 'react';

// Theme definitions - each theme has a complete color system
export const themes = {
  // Default - Classic Islamic Green & Gold
  classic: {
    id: 'classic',
    name: 'Classic Green',
    description: 'Traditional Islamic green with gold accents',
    preview: { primary: '#1D4B44', accent: '#D4AF37', bg: '#FAF7F2' },
    colors: {
      'deep-green': '#1D4B44',
      'medium-green': '#2A6D62',
      'light-green': '#5C9992',
      'gold': '#D4AF37',
      'light-gold': '#E9D68A',
      'soft-ivory': '#FAF7F2',
      'charcoal': '#333333',
      'light-gray': '#F0F0F0',
      'heading': '#1D4B44',
      'button-bg': '#1D4B44',
      'button-text': '#FFFFFF',
      'header-text': '#FFFFFF',
      'card-bg': '#FFFFFF',
      'card-border': '#E5E7EB',
      'input-border': '#D1D5DB',
      'input-bg': '#FFFFFF',
      'danger': '#DC2626',
      'danger-bg': '#FEF2F2',
      'verse-label-bg': '#F0F0F0',
      'verse-label-text': '#1D4B44',
      'search-hover': '#C7D2FE',
      'gradient-start': '#1D4B44',
      'gradient-mid': '#D4AF37',
      'gradient-end': '#1D4B44',
    }
  },

  // Midnight Blue - Deep navy with silver moonlight
  midnight: {
    id: 'midnight',
    name: 'Midnight Sky',
    description: 'Deep navy blue with silver moonlight accents',
    preview: { primary: '#0F1B2D', accent: '#C0C0C0', bg: '#1A2744' },
    colors: {
      'deep-green': '#0F1B2D',
      'medium-green': '#1A2744',
      'light-green': '#2D4A7A',
      'gold': '#C0C0C0',
      'light-gold': '#D4D8E0',
      'soft-ivory': '#111827',
      'charcoal': '#E2E8F0',
      'light-gray': '#1E293B',
      'heading': '#C0C0C0',
      'button-bg': '#2D4A7A',
      'button-text': '#E2E8F0',
      'header-text': '#E2E8F0',
      'card-bg': '#1E293B',
      'card-border': '#334155',
      'input-border': '#475569',
      'input-bg': '#1E293B',
      'danger': '#F87171',
      'danger-bg': '#1C1917',
      'verse-label-bg': '#1E293B',
      'verse-label-text': '#94A3B8',
      'search-hover': '#334155',
      'gradient-start': '#0F1B2D',
      'gradient-mid': '#C0C0C0',
      'gradient-end': '#0F1B2D',
    }
  },

  // Desert Sand - Warm earthy tones
  desert: {
    id: 'desert',
    name: 'Desert Sand',
    description: 'Warm earthy tones inspired by the desert',
    preview: { primary: '#8B5E3C', accent: '#D4A574', bg: '#FFF8F0' },
    colors: {
      'deep-green': '#6B4226',
      'medium-green': '#8B5E3C',
      'light-green': '#B8865A',
      'gold': '#D4A574',
      'light-gold': '#F0D9B5',
      'soft-ivory': '#FFF8F0',
      'charcoal': '#3E2723',
      'light-gray': '#F5E6D3',
      'heading': '#6B4226',
      'button-bg': '#6B4226',
      'button-text': '#FFF8F0',
      'header-text': '#FFF8F0',
      'card-bg': '#FFFFFF',
      'card-border': '#E8D5C4',
      'input-border': '#D4B896',
      'input-bg': '#FFFBF5',
      'danger': '#C62828',
      'danger-bg': '#FFF0F0',
      'verse-label-bg': '#F5E6D3',
      'verse-label-text': '#6B4226',
      'search-hover': '#F0D9B5',
      'gradient-start': '#6B4226',
      'gradient-mid': '#D4A574',
      'gradient-end': '#6B4226',
    }
  },

  // Ocean Breeze - Cool teals and aqua
  ocean: {
    id: 'ocean',
    name: 'Ocean Breeze',
    description: 'Cool calming teal and aqua tones',
    preview: { primary: '#0D4F4F', accent: '#2DD4BF', bg: '#F0FDFA' },
    colors: {
      'deep-green': '#0D4F4F',
      'medium-green': '#0F766E',
      'light-green': '#14B8A6',
      'gold': '#2DD4BF',
      'light-gold': '#99F6E4',
      'soft-ivory': '#F0FDFA',
      'charcoal': '#1A3636',
      'light-gray': '#CCFBF1',
      'heading': '#0D4F4F',
      'button-bg': '#0D4F4F',
      'button-text': '#F0FDFA',
      'header-text': '#F0FDFA',
      'card-bg': '#FFFFFF',
      'card-border': '#99F6E4',
      'input-border': '#5EEAD4',
      'input-bg': '#F0FDFA',
      'danger': '#DC2626',
      'danger-bg': '#FEF2F2',
      'verse-label-bg': '#CCFBF1',
      'verse-label-text': '#0D4F4F',
      'search-hover': '#99F6E4',
      'gradient-start': '#0D4F4F',
      'gradient-mid': '#2DD4BF',
      'gradient-end': '#0D4F4F',
    }
  },

  // Royal Purple - Regal and elegant
  royal: {
    id: 'royal',
    name: 'Royal Purple',
    description: 'Regal purple with warm gold highlights',
    preview: { primary: '#3B1261', accent: '#E8B931', bg: '#FAF5FF' },
    colors: {
      'deep-green': '#3B1261',
      'medium-green': '#5B21B6',
      'light-green': '#7C3AED',
      'gold': '#E8B931',
      'light-gold': '#FDE68A',
      'soft-ivory': '#FAF5FF',
      'charcoal': '#2E1065',
      'light-gray': '#EDE9FE',
      'heading': '#3B1261',
      'button-bg': '#3B1261',
      'button-text': '#FAF5FF',
      'header-text': '#FAF5FF',
      'card-bg': '#FFFFFF',
      'card-border': '#DDD6FE',
      'input-border': '#C4B5FD',
      'input-bg': '#FAF5FF',
      'danger': '#DC2626',
      'danger-bg': '#FEF2F2',
      'verse-label-bg': '#EDE9FE',
      'verse-label-text': '#3B1261',
      'search-hover': '#DDD6FE',
      'gradient-start': '#3B1261',
      'gradient-mid': '#E8B931',
      'gradient-end': '#3B1261',
    }
  },

  // AMOLED Dark - True black for OLED screens
  amoled: {
    id: 'amoled',
    name: 'AMOLED Dark',
    description: 'True black with emerald green accents',
    preview: { primary: '#000000', accent: '#10B981', bg: '#000000' },
    colors: {
      'deep-green': '#000000',
      'medium-green': '#111111',
      'light-green': '#10B981',
      'gold': '#10B981',
      'light-gold': '#34D399',
      'soft-ivory': '#000000',
      'charcoal': '#E5E7EB',
      'light-gray': '#111111',
      'heading': '#10B981',
      'button-bg': '#1F1F1F',
      'button-text': '#10B981',
      'header-text': '#E5E7EB',
      'card-bg': '#0A0A0A',
      'card-border': '#1F1F1F',
      'input-border': '#333333',
      'input-bg': '#0A0A0A',
      'danger': '#F87171',
      'danger-bg': '#1C1917',
      'verse-label-bg': '#111111',
      'verse-label-text': '#10B981',
      'search-hover': '#1F1F1F',
      'gradient-start': '#000000',
      'gradient-mid': '#10B981',
      'gradient-end': '#000000',
    }
  },

  // Rose Garden - Soft pink and warm tones
  rose: {
    id: 'rose',
    name: 'Rose Garden',
    description: 'Soft rose and blush tones with warm accents',
    preview: { primary: '#881337', accent: '#FB7185', bg: '#FFF1F2' },
    colors: {
      'deep-green': '#881337',
      'medium-green': '#9F1239',
      'light-green': '#E11D48',
      'gold': '#FB7185',
      'light-gold': '#FECDD3',
      'soft-ivory': '#FFF1F2',
      'charcoal': '#4C0519',
      'light-gray': '#FFE4E6',
      'heading': '#881337',
      'button-bg': '#881337',
      'button-text': '#FFF1F2',
      'header-text': '#FFF1F2',
      'card-bg': '#FFFFFF',
      'card-border': '#FECDD3',
      'input-border': '#FDA4AF',
      'input-bg': '#FFF1F2',
      'danger': '#DC2626',
      'danger-bg': '#FEF2F2',
      'verse-label-bg': '#FFE4E6',
      'verse-label-text': '#881337',
      'search-hover': '#FECDD3',
      'gradient-start': '#881337',
      'gradient-mid': '#FB7185',
      'gradient-end': '#881337',
    }
  },

  // Slate Minimal - Clean modern monochrome
  slate: {
    id: 'slate',
    name: 'Slate Minimal',
    description: 'Clean monochrome design with subtle blue tints',
    preview: { primary: '#1E293B', accent: '#3B82F6', bg: '#F8FAFC' },
    colors: {
      'deep-green': '#1E293B',
      'medium-green': '#334155',
      'light-green': '#64748B',
      'gold': '#3B82F6',
      'light-gold': '#93C5FD',
      'soft-ivory': '#F8FAFC',
      'charcoal': '#1E293B',
      'light-gray': '#E2E8F0',
      'heading': '#1E293B',
      'button-bg': '#1E293B',
      'button-text': '#F8FAFC',
      'header-text': '#F8FAFC',
      'card-bg': '#FFFFFF',
      'card-border': '#E2E8F0',
      'input-border': '#CBD5E1',
      'input-bg': '#F8FAFC',
      'danger': '#DC2626',
      'danger-bg': '#FEF2F2',
      'verse-label-bg': '#E2E8F0',
      'verse-label-text': '#1E293B',
      'search-hover': '#DBEAFE',
      'gradient-start': '#1E293B',
      'gradient-mid': '#3B82F6',
      'gradient-end': '#1E293B',
    }
  },
};

const THEME_STORAGE_KEY = 'quranReaderTheme';

export const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(() => {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    return saved && themes[saved] ? saved : 'classic';
  });

  // Apply theme CSS variables whenever theme changes
  useEffect(() => {
    const theme = themes[currentTheme];
    if (!theme) return;

    const root = document.documentElement;
    Object.entries(theme.colors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}`, value);
    });

    // Also set the gradient
    root.style.setProperty('--gradient-start', theme.colors['gradient-start']);
    root.style.setProperty('--gradient-mid', theme.colors['gradient-mid']);
    root.style.setProperty('--gradient-end', theme.colors['gradient-end']);

    // Keep the browser/OS chrome (address bar, task switcher) in step with the theme
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', theme.colors['deep-green']);

    // Save to localStorage
    localStorage.setItem(THEME_STORAGE_KEY, currentTheme);
  }, [currentTheme]);

  const changeTheme = (themeId) => {
    if (themes[themeId]) {
      setCurrentTheme(themeId);
    }
  };

  return (
    <ThemeContext.Provider value={{ currentTheme, changeTheme, themes }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};